// src/store/cardStore.ts
// Current time: Wednesday, August 20, 2025 at 7:48:14 PM UTC
/**
 * .tuMetrics {"tu1": 0.83, "tu2": 0.88, "tu3": 7, "notes": "Card store subscription bootstrap guard prevents duplicate initialize while loading", "tuVersion": "2.5"}
 * .help "Central card data store for loading, filtering, realtime updates, and card mutations."
 */
import { create } from 'zustand';
import { Card, CardType, CardStatus, FeedEventType } from '../types';
import useUserStore from './userStore';
import useFeedStore from './feedStore';
import useFollowStore from './followStore';
import useTeamStore from './teamStore';
import { supabase } from '../lib/supabase'; // Import supabase client for storage
import type { RealtimeChannel } from '@supabase/supabase-js';
import { parseISO, isPast } from 'date-fns';

import {
  mapCardFromDB,
  mapCardToDB,
  mapCardUpdatesToDB,
  fetchCards,
  insertCard,
  updateCardById,
  deleteCardById,
  updateCardPositions,
  rpcConvertCardType,
  fetchCardById,
} from '../lib/supabase/queries/card-queries';
import type { CardRow } from '../lib/supabase/queries/card-mappers';
import { followRefsFromCard } from '../features/feed/followMatch';
import { formatTimeForHistory, timeFieldsOnMoveToDone, timeFieldsOnReopenFromDone } from './card/timeTracking';
import { calculateNextOccurrenceDate } from './card/recurrence';
import {
  allApproversApproved,
  diffApprovalWorkflow,
  pendingApproverIds,
  reviewGateNotifyKind,
} from './card/approvals';
import { errorMessage } from './card/errorMessage';
import { cardTypeLabel, cardTypeTitle, feedKindForCardType, isWorkCardType } from '../utils/cardTypeUtils';
import { maybeWakeTeamBotsOnAssign } from '../lib/teambots/wakeCardAssign';

// FeedEventType (types/index.ts, owned by another workstream) is missing several event
// names this store has always emitted: idea_proposed, idea_updated, idea_deleted,
// task_completed_by_assignee. Widen locally until they are added there; runtime unchanged.
type CardFeedEventType =
  | FeedEventType
  | 'idea_proposed'
  | 'idea_updated'
  | 'idea_deleted'
  | 'task_completed_by_assignee';
const feedEventType = (t: CardFeedEventType): FeedEventType => t as FeedEventType;

/** Ping pending approvers when Review starts (or Done skips Review). Used by updateCard + drag moves. */
function notifyApproversReviewGate(opts: {
  card: Pick<Card, 'id' | 'title' | 'approvalWorkflow' | 'sprintId' | 'roadmapId'>;
  oldStatus: CardStatus;
  newStatus: CardStatus;
  actorId: string;
  actorName: string;
}) {
  const kind = reviewGateNotifyKind(opts.oldStatus, opts.newStatus);
  if (!kind) return;
  const description =
    kind === 'entered_review'
      ? `${opts.actorName} moved "${opts.card.title}" to Review`
      : `${opts.actorName} completed "${opts.card.title}" — please sign off`;
  for (const userId of pendingApproverIds(opts.card.approvalWorkflow, opts.actorId)) {
    useFeedStore.getState().addEvent({
      type: 'approval_requested',
      title: 'Ready for your review',
      description,
      userId,
      initiatorId: opts.actorId,
      cardId: opts.card.id,
      sprintId: opts.card.sprintId,
      roadmapId: opts.card.roadmapId,
    });
  }
}

interface CardState {
  _allCards: Card[]; // Holds all cards fetched from DB
  cards: Card[]; // Filtered view of _allCards based on selectedTeamId
  isLoading: boolean;
  error: string | null;
  initialLoadComplete: boolean;
  isDragInProgress: boolean; // Flag to prevent filtering during drag operations
  lastFetchedAt: number | null; // Timestamp of last fetch for staleness check
  realtimeChannel: RealtimeChannel | null;

  /** Internal: recompute `cards` from `_allCards` using the selected team + draft rules */
  _applyFilter: () => void;

  initializeCards: () => Promise<void>;
  refreshCardsIfStale: () => Promise<void>;
  setupRealtimeSubscription: () => Promise<void>;
  cleanupRealtimeSubscription: () => void;
  setSelectedTeamId: (teamId: string | null) => void;
  addCard: (card: Omit<Card, 'id' | 'createdAt' | 'status' | 'blocked' | 'dependsOn'>) => Promise<Card | undefined>; // December 9, 2025: Return created card for draft flow
  updateCard: (cardId: string, updates: Partial<Card>, skipFilter?: boolean) => Promise<void>;
  updateTimeTrackingOptimistic: (cardId: string, updates: Partial<Card>, timeTrackingAction?: { action: 'start' | 'pause' | 'resume' | 'stop'; totalSecondsAfter?: number }) => Promise<void>;
  moveCard: (cardId: string, updates: { status?: CardStatus; sprintId?: string | null; roadmapId?: string | null }) => Promise<void>;
  moveCardOptimistic: (cardId: string, targetStatus: CardStatus, targetIndex: number) => Promise<void>;
  toggleCardBlocked: (cardId: string, reason?: string) => Promise<void>;
  deleteCard: (cardId: string) => Promise<void>;
  voteOnCard: (cardId: string, userId: string, voteType: 'up' | 'down') => Promise<void>;
  convertCardType: (cardId: string, newType: CardType) => Promise<void>;
  addDependency: (cardId: string, dependencyId: string) => Promise<void>;
  removeDependency: (cardId: string, dependencyId: string) => Promise<void>;
  checkDependencies: (cardId: string) => boolean;
  getBlockingCards: (cardId: string) => Card[];
  getUserVote: (cardId: string, userId: string) => 'up' | 'down' | null;
  reorderCards: (columnId: CardStatus, orderedCardIds: string[]) => Promise<void>;
  updateCardPosition: (cardId: string, direction: 'up' | 'down') => Promise<void>;
  linkGitHubIssue: (cardId: string, issueData: NonNullable<Card['githubIssue']>) => Promise<void>;
  fetchCardById: (cardId: string) => Promise<Card>;
  refreshCardsFromDB: () => Promise<void>;
  setDragInProgress: (inProgress: boolean) => void;
  // December 4, 2025: Archive support
  archiveCard: (cardId: string) => Promise<void>;
  unarchiveCard: (cardId: string) => Promise<void>;
}

const useCardStore = create<CardState>((set, get) => ({
  _allCards: [],
  cards: [],
  isLoading: false,
  error: null,
  initialLoadComplete: false,
  isDragInProgress: false,
  lastFetchedAt: null,
  realtimeChannel: null,

  // Draft visibility only. Browse team filters are per-view (TeamFilterChip).
  _applyFilter: () => {
    const { _allCards, isDragInProgress } = get();
    if (isDragInProgress) return;

    const currentUser = useUserStore.getState().currentUser;
    let filtered = _allCards;
    if (currentUser) {
      filtered = filtered.filter((card) =>
        !card.is_draft || card.createdById === currentUser.id
      );
    }
    set({ cards: filtered });
  },

  initializeCards: async () => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;

      if (!currentUser?.companyId) {
        console.warn('initializeCards: No current user companyId found. Skipping card initialization.');
        set({ _allCards: [], cards: [], isLoading: false, initialLoadComplete: true });
        return;
      }

      // Fetch all cards for the company (no team filter here)
      const allCardsFromDb = await fetchCards(currentUser.companyId, null);
      set({ _allCards: allCardsFromDb, isLoading: false, lastFetchedAt: Date.now(), initialLoadComplete: true });
      get()._applyFilter();
    } catch (error: unknown) {
      console.error('Error loading cards:', error);
      set({ error: `Failed to load cards: ${errorMessage(error)}`, isLoading: false });
    }
  },

  setSelectedTeamId: (teamId) => {
    // Sync with teamStore (shared across all pages)
    useTeamStore.getState().setSelectedTeamId(teamId);
    get()._applyFilter();
  },

  addCard: async (card) => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');
      if (!currentUser.companyId) throw new Error('Current user is not associated with a company.');

      const cardData: Omit<Card, 'id' | 'createdAt'> = {
        ...card,
        status: 'todo',
        blocked: false,
        dependsOn: [],
        createdById: currentUser.id,
        // IMPORTANT: Only set when agent path passes it in explicitly
        requestedById: card.requestedById,
        sentimentVotes: { up: 0, down: 0, user_votes: [] },
        // Prefer explicit card.teamId; else user's primary team. Never the old global browse selector / Company.
        teamId: card.teamId ?? currentUser.team ?? undefined,
        isRecurring: card.isRecurring || false,
        recurrencePattern: card.recurrencePattern || undefined,
        nextOccurrenceDate: undefined,
        approvalWorkflow: card.approvalWorkflow || [], // NEW: Initialize approvalWorkflow
      };

      // Ideas land at the bottom of the assignee's To Do so All doesn't dump them on top.
      if (card.type === 'idea' && cardData.position == null) {
        const scopeId = card.assigneeId ?? currentUser.id;
        const siblings = get()._allCards.filter(
          (c) =>
            !c.archivedAt
            && c.status === 'todo'
            && (c.assigneeId === scopeId
              || (Boolean(c.is_draft) && c.createdById === currentUser.id)),
        );
        const maxPos = siblings.reduce((m, c) => Math.max(m, c.position ?? -1), -1);
        cardData.position = maxPos + 1;
      }

      if (cardData.isRecurring && cardData.recurrencePattern) {
        cardData.nextOccurrenceDate = calculateNextOccurrenceDate(new Date(), cardData.recurrencePattern);
      }

      const mappedCard = await insertCard(mapCardToDB(cardData, currentUser.companyId));

      // December 9, 2025: Skip notifications for draft cards
      if (!card.is_draft) {
        // Enhanced creation event with requester information
        let creationDescription = `${currentUser.name} ${isWorkCardType(card.type) ? 'created' : 'proposed'} "${card.title}"`;
        
        // If created by an agent and there's a requester, include that information
        if (currentUser.isAgent && card.requestedById) {
          const requester = useUserStore.getState().allUsers.find(u => u.id === card.requestedById);
          const requesterName = requester ? requester.name : 'Former User';
          creationDescription = `${currentUser.name} ${isWorkCardType(card.type) ? 'created' : 'proposed'} "${card.title}" (requested by ${requesterName})`;
        }

        useFeedStore.getState().addEvent({
          type: feedEventType(feedKindForCardType(card.type) === 'task' ? 'task_created' : 'idea_proposed'),
          title: 'Card Created',
          description: creationDescription,
          userId: currentUser.id,
          initiatorId: currentUser.id,
          cardId: mappedCard.id,
          sprintId: mappedCard.sprintId,
          roadmapId: mappedCard.roadmapId,
          projectId: mappedCard.projectId ?? undefined,
        });

        // December 4, 2025: Send notifications for BOTH tasks AND ideas when assigned
        if (card.assigneeId && card.assigneeId !== currentUser.id) {
          // This event is for the assignee, so it might use "you" in notifications.
          // However, for history, we want the literal name.
          const assigneeUser = useUserStore.getState().allUsers.find(u => u.id === card.assigneeId);
          const assigneeName = assigneeUser ? assigneeUser.name : 'Former User';
          const typeLabel = cardTypeLabel(card.type);

          const description = `${currentUser.name} assigned ${typeLabel} "${card.title}" to ${assigneeName}`;
          console.log(`cardStore: addCard - ${typeLabel} Assigned event description (to be sent):`, description);

          useFeedStore.getState().addEvent({
            type: feedEventType(feedKindForCardType(card.type) === 'task' ? 'task_updated' : 'idea_updated'),
            title: `${cardTypeTitle(card.type)} Assigned`,
            // Ensure the description always uses the literal assignee name for history
            description: description,
            userId: card.assigneeId, // Recipient of the notification
            initiatorId: currentUser.id,
            cardId: mappedCard.id,
            sprintId: mappedCard.sprintId,
            roadmapId: mappedCard.roadmapId,
            projectId: mappedCard.projectId ?? undefined,
          });

          maybeWakeTeamBotsOnAssign(mappedCard, card.assigneeId);
        }
      }

      set((state) => ({
        _allCards: [mappedCard, ...state._allCards],
        isLoading: false,
      }));
      get()._applyFilter();
      
      // December 9, 2025: Return created card for draft flow
      return mappedCard;
    } catch (error: unknown) {
      console.error('Error adding card:', error);
      set({ error: `Failed to add card: ${errorMessage(error)}`, isLoading: false });
      return undefined;
    }
  },

  updateCard: async (cardId, updates, skipFilter = false) => {
    console.log('📝 [cardStore] updateCard - START', { cardId, skipFilter, updates: Object.keys(updates) });
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      const oldCard = get()._allCards.find((c) => c.id === cardId);
      if (!oldCard) throw new Error('Card not found');

      // December 9, 2025: Debug logging for draft idea updates
      if (oldCard.is_draft && oldCard.type === 'idea') {
        console.log('🔍 [cardStore] Updating draft IDEA:', {
          cardId,
          oldType: oldCard.type,
          newType: updates.type,
          oldAssigneeId: oldCard.assigneeId,
          newAssigneeId: updates.assigneeId,
          oldRequestedById: oldCard.requestedById,
          newRequestedById: updates.requestedById,
          createdById: oldCard.createdById,
        });
      }

      console.log('📝 [cardStore] updateCard - Old card state:', { 
        startedAt: oldCard.startedAt, 
        pausedAt: oldCard.pausedAt, 
        lastWorkSessionStart: oldCard.lastWorkSessionStart,
        status: oldCard.status 
      });

      // Reassignment: inProgress means "I am working this." The new owner has not
      // started — drop to todo at the top of that column so it does not sit on
      // their In Progress board. Explicit status in the same update wins; done /
      // archived / already-todo are left alone. Unassign does not force a reset.
      const assigneeChanging =
        updates.assigneeId !== undefined && updates.assigneeId !== oldCard.assigneeId;
      if (
        assigneeChanging
        && updates.assigneeId
        && updates.status === undefined
        && oldCard.status === 'inProgress'
      ) {
        updates = { ...updates, status: 'todo', position: 0 };
        const todoSiblings = get()._allCards
          .filter((c) => c.status === 'todo' && c.id !== cardId)
          .sort((a, b) => (a.position || 0) - (b.position || 0));
        const positionUpdates = todoSiblings.map((c, idx) => ({ id: c.id, position: idx + 1 }));
        if (positionUpdates.length > 0) {
          updateCardPositions(positionUpdates).catch((err) =>
            console.error('Error shifting todo positions after reassignment:', err),
          );
        }
      }

      // Review gate: last required approval while in Review → Done (same write).
      if (updates.approvalWorkflow !== undefined && updates.status === undefined) {
        const nextWorkflow = updates.approvalWorkflow || [];
        const effectiveStatus = oldCard.status;
        if (effectiveStatus === 'under_review' && allApproversApproved(nextWorkflow)) {
          updates = { ...updates, status: 'done' };
        }
      }

      // No-op guard: opening a card used to autosave identical (or TipTap-normalized)
      // description HTML and still emit "X updated title." into the stream footer.
      const updateKeys = Object.keys(updates) as (keyof Card)[];
      const isNoOp = updateKeys.every((key) => {
        const next = updates[key];
        const prev = oldCard[key];
        if (next === prev) return true;
        if (next == null && prev == null) return true;
        if (Array.isArray(next) && Array.isArray(prev)) {
          return JSON.stringify(next) === JSON.stringify(prev);
        }
        if (typeof next === 'object' && typeof prev === 'object' && next && prev) {
          return JSON.stringify(next) === JSON.stringify(prev);
        }
        return false;
      });
      if (isNoOp) {
        set({ isLoading: false });
        return;
      }

      const dbUpdates = mapCardUpdatesToDB(updates);
      
      console.log('[cardStore] updateCard - dbUpdates:', JSON.stringify(dbUpdates, null, 2));
      console.log('[cardStore] updateCard - github_issue in dbUpdates:', dbUpdates.github_issue);

      // Handle recurring task fields
      if (updates.isRecurring !== undefined) {
        dbUpdates.is_recurring = updates.isRecurring;
        if (updates.isRecurring) {
          dbUpdates.recurrence_pattern = updates.recurrencePattern
            ? JSON.stringify(updates.recurrencePattern)
            : null;

          const baseDateForCalculation =
            oldCard.nextOccurrenceDate && !isPast(parseISO(oldCard.nextOccurrenceDate))
              ? parseISO(oldCard.nextOccurrenceDate)
              : new Date();

          dbUpdates.next_occurrence_date = calculateNextOccurrenceDate(
            baseDateForCalculation,
            updates.recurrencePattern || {},
          );
        } else {
          dbUpdates.recurrence_pattern = null;
          dbUpdates.next_occurrence_date = null;
        }
      } else if (oldCard.isRecurring && updates.recurrencePattern !== undefined) {
        dbUpdates.recurrence_pattern = updates.recurrencePattern
          ? JSON.stringify(updates.recurrencePattern)
          : null;

        const baseDateForCalculation =
          oldCard.nextOccurrenceDate && !isPast(parseISO(oldCard.nextOccurrenceDate))
            ? parseISO(oldCard.nextOccurrenceDate)
            : new Date();

        dbUpdates.next_occurrence_date = calculateNextOccurrenceDate(
          baseDateForCalculation,
          updates.recurrencePattern || {},
        );
      }

      const updatedCard = await updateCardById(cardId, dbUpdates);
      
      console.log('[cardStore] updateCard - updatedCard from DB:', {
        id: updatedCard.id,
        githubIssue: updatedCard.githubIssue,
        githubIssueIsNull: updatedCard.githubIssue === null || updatedCard.githubIssue === undefined,
        assigneeId: updatedCard.assigneeId,
        createdById: updatedCard.createdById,
        status: updatedCard.status
      });

      // Update the store immediately
      console.log('📝 [cardStore] updateCard - Updating store, skipFilter:', skipFilter);
      const updatedAllCards = get()._allCards.map((card) => (card.id === cardId ? updatedCard : card));
      console.log('📝 [cardStore] updateCard - Updated card state:', { 
        startedAt: updatedCard.startedAt, 
        pausedAt: updatedCard.pausedAt, 
        lastWorkSessionStart: updatedCard.lastWorkSessionStart,
        status: updatedCard.status 
      });
      
      set({ _allCards: updatedAllCards });
      console.log('📝 [cardStore] updateCard - _allCards updated, length:', updatedAllCards.length);
      
      // Skip filter for time tracking updates to prevent redraws
      if (!skipFilter) {
        console.log('📝 [cardStore] updateCard - Calling _applyFilter (skipFilter=false)');
        get()._applyFilter();
        console.log('📝 [cardStore] updateCard - _applyFilter completed');
      } else {
        console.log('📝 [cardStore] updateCard - Skipping _applyFilter, updating cards array directly');
        // When skipping filter, still update cards array to keep UI in sync
        const updatedCards = updatedAllCards;
        
        console.log('📝 [cardStore] updateCard - Setting cards array directly, length:', updatedCards.length);
        set({ cards: updatedCards });
        console.log('📝 [cardStore] updateCard - cards array updated');
      }
      
      console.log('[cardStore] updateCard - Store updated, card in store now:', {
        id: get()._allCards.find(c => c.id === cardId)?.id,
        githubIssue: get()._allCards.find(c => c.id === cardId)?.githubIssue,
        githubIssueIsNull: get()._allCards.find(c => c.id === cardId)?.githubIssue === null || 
                           get()._allCards.find(c => c.id === cardId)?.githubIssue === undefined
      });

      const changes: string[] = [];
      
      if (updates.title !== undefined && updates.title !== oldCard.title) {
        changes.push(`title from "${oldCard.title}" to "${updates.title}"`);
      }
      if (updates.description !== undefined && updates.description !== oldCard.description) {
        changes.push(`description changed`);
      }
      if (updates.status !== undefined && updates.status !== oldCard.status) {
        changes.push(`status from "${oldCard.status}" to "${updates.status}"`);
      }
      if (updates.priority !== undefined && updates.priority !== oldCard.priority) {
        changes.push(`priority from "${oldCard.priority}" to "${updates.priority}"`);
      }
      // FIX: Ensure assignee name is always literal for history, even for self-assignment
      if (updates.assigneeId !== undefined && updates.assigneeId !== oldCard.assigneeId) {
        const assigneeUser = useUserStore.getState().allUsers.find(u => u.id === updates.assigneeId);
        const oldAssigneeUser = oldCard.assigneeId ? useUserStore.getState().allUsers.find(u => u.id === oldCard.assigneeId) : null;
        const assigneeName = assigneeUser ? assigneeUser.name : 'Unassigned';
        const oldAssigneeName = oldAssigneeUser ? oldAssigneeUser.name : 'Unassigned';
        changes.push(
          `assignee from "${oldAssigneeName}" to "${assigneeName}"`,
        );
      }
      
      if (updates.complexity !== undefined && updates.complexity !== oldCard.complexity) {
        if (updates.complexity == null) {
          if (oldCard.complexity != null) changes.push(`cleared story points (${oldCard.complexity}pt)`);
        } else {
          changes.push(
            oldCard.complexity != null
              ? `story points from ${oldCard.complexity}pt to ${updates.complexity}pt`
              : `set story points to ${updates.complexity}pt`,
          );
        }
      }
      if (updates.dueDate !== undefined) {
        const oldDueDate = oldCard.dueDate ? new Date(oldCard.dueDate).toLocaleDateString() : 'N/A';
        const newDueDate = updates.dueDate ? new Date(updates.dueDate).toLocaleDateString() : 'N/A';
        if (oldDueDate !== newDueDate) {
          changes.push(`due date from "${oldDueDate}" to "${newDueDate}"`);
        }
      }
      if (updates.timeEstimateDays !== undefined && updates.timeEstimateDays !== oldCard.timeEstimateDays) {
        // Switching to story points clears days — don't log "null days".
        if (updates.timeEstimateDays == null) {
          if (oldCard.timeEstimateDays != null) {
            changes.push(`cleared time estimate (${oldCard.timeEstimateDays} days)`);
          }
        } else {
          changes.push(
            oldCard.timeEstimateDays != null
              ? `time estimate from ${oldCard.timeEstimateDays} days to ${updates.timeEstimateDays} days`
              : `set time estimate to ${updates.timeEstimateDays} days`,
          );
        }
      }
      if (updates.blocked !== undefined && updates.blocked !== oldCard.blocked) {
        changes.push(`marked as ${updates.blocked ? 'blocked' : 'unblocked'}`);
      }
      if (updates.teamId !== undefined && updates.teamId !== oldCard.teamId) {
        changes.push('team changed');
      }
      if (updates.projectId !== undefined && updates.projectId !== oldCard.projectId) {
        changes.push('project changed');
      }
      if (updates.type !== undefined && updates.type !== oldCard.type) {
        changes.push(`type from "${oldCard.type}" to "${updates.type}"`);
      }
      if (updates.scheduledDate !== undefined && updates.scheduledDate !== oldCard.scheduledDate) {
        changes.push('schedule changed');
      }
      if (updates.scheduledStartTime !== undefined && updates.scheduledStartTime !== oldCard.scheduledStartTime) {
        changes.push('schedule time changed');
      }
      if (
        updates.scheduledDurationMinutes !== undefined
        && updates.scheduledDurationMinutes !== oldCard.scheduledDurationMinutes
      ) {
        changes.push('schedule duration changed');
      }
      if (updates.reminderDays !== undefined && updates.reminderDays !== oldCard.reminderDays) {
        changes.push('reminder changed');
      }
      if (updates.tags !== undefined) {
        console.log('cardStore: updateCard - Type of oldCard.tags:', typeof oldCard.tags, 'Content:', oldCard.tags);
        const oldTags = new Set(oldCard.tags);
        const newTags = new Set(updates.tags);
        const addedTags = [...newTags].filter((tag) => !oldTags.has(tag));
        const removedTags = [...oldTags].filter((tag) => !newTags.has(tag));

        if (addedTags.length > 0) {
          changes.push(`added tag(s): ${addedTags.join(', ')}`);
        }
        if (removedTags.length > 0) {
          changes.push(`removed tag(s): ${removedTags.join(', ')}`);
        }
      }

      // Only audit when something human-readable changed. Empty "X updated title."
      // was the smoking gun for phantom opens (description autosave with no field diff).
      if (!oldCard.is_draft && changes.length > 0) {
        const eventDescription = `${currentUser.name} updated "${updatedCard.title}": ${changes.join(', ')}.`;

        // Build metadata with before/after for meaningful fields
        const metadata: Record<string, unknown> = {};
        if (updates.description !== undefined && updates.description !== oldCard.description) {
          metadata.previous_state = {
            description: oldCard.description || '',
          };
          metadata.new_state = {
            description: updates.description || '',
          };
        }
        
        useFeedStore.getState().addEvent({
          type: feedEventType(feedKindForCardType(oldCard.type) === 'task' ? 'task_updated' : 'idea_updated'),
          title: 'Card Updated',
          description: eventDescription,
          userId: undefined, // AUDIT: No notification, just history (feedStore treats null/undefined the same)
          initiatorId: currentUser.id, // Track who made the change
          cardId: updatedCard.id,
          sprintId: updatedCard.sprintId,
          roadmapId: updatedCard.roadmapId,
          metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
        });

        const followWorthy = changes.some((c) =>
          c.startsWith('status from')
          || c.startsWith('assignee from')
          || c.startsWith('title from')
          || c.startsWith('priority from')
          || c.startsWith('marked as')
        );
        if (followWorthy && !skipFilter) {
          try {
            await useFollowStore.getState().notifyFollowers(
              followRefsFromCard(updatedCard),
              [currentUser.id, updates.assigneeId],
              {
                type: feedEventType(feedKindForCardType(oldCard.type) === 'task' ? 'task_updated' : 'idea_updated'),
                title: 'Card Updated',
                description: eventDescription,
                initiatorId: currentUser.id,
                cardId: updatedCard.id,
                sprintId: updatedCard.sprintId,
                roadmapId: updatedCard.roadmapId,
                projectId: updatedCard.projectId ?? undefined,
              },
            );
          } catch (followErr) {
            console.warn('Follower notify on update failed (non-critical):', followErr);
          }
        }
      }

      // Create specific feed events for different types of changes
      
      // Status change tracking
      if (updates.status !== undefined && updates.status !== oldCard.status) {
        const statusDisplayNames = {
          'todo': 'To Do',
          'inProgress': 'In Progress', 
          'done': 'Done',
          'archived': 'Archived',
          'under_review': 'Under Review'
        };
        
        const oldStatusName = statusDisplayNames[oldCard.status as keyof typeof statusDisplayNames] || oldCard.status;
        const newStatusName = statusDisplayNames[updates.status as keyof typeof statusDisplayNames] || updates.status;
        
        // If task/idea is marked as done and was assigned to someone else, notify the creator
        // December 9, 2025: Support both tasks and ideas, skip for drafts
        // Use oldCard for assignee/creator check since updatedCard might not have all fields populated
        const cardToCheck = updatedCard.assigneeId && updatedCard.createdById ? updatedCard : oldCard;
        
        if (updates.status === 'done' && cardToCheck.assigneeId && cardToCheck.createdById && !oldCard.is_draft) {
          console.log('🔔 CardStore: Card marked as done, checking if notification needed:', {
            cardId: updatedCard.id,
            cardTitle: updatedCard.title,
            cardType: updatedCard.type,
            assigneeId: cardToCheck.assigneeId,
            createdById: cardToCheck.createdById,
            areDifferent: cardToCheck.createdById !== cardToCheck.assigneeId,
            oldStatus: oldCard.status,
            newStatus: updates.status,
            usingOldCard: cardToCheck === oldCard
          });
          
          // Only notify if creator is different from assignee (not self-assignment)
          if (cardToCheck.createdById !== cardToCheck.assigneeId) {
            const assigneeUser = useUserStore.getState().allUsers.find(u => u.id === cardToCheck.assigneeId);
            const assigneeName = assigneeUser ? assigneeUser.name : 'Someone';
            const typeLabel = cardTypeLabel(updatedCard.type);
            
            console.log('🔔 CardStore: Creating completion notification for creator:', {
              creatorId: cardToCheck.createdById,
              assigneeName: assigneeName,
              cardTitle: updatedCard.title,
              cardType: typeLabel
            });
            
            useFeedStore.getState().addEvent({
              type: feedEventType(feedKindForCardType(updatedCard.type) === 'task' ? 'task_completed_by_assignee' : 'idea_updated'),
              title: `${typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)} Completed`,
              description: `${assigneeName} completed ${typeLabel} "${updatedCard.title}"`,
              userId: cardToCheck.createdById, // Notify the creator
              initiatorId: currentUser.id, // Person who marked it done (usually the assignee)
              cardId: updatedCard.id,
              sprintId: updatedCard.sprintId,
              roadmapId: updatedCard.roadmapId,
            });
          } else {
            console.log('🔔 CardStore: Skipping notification - self-assignment (creator === assignee)');
          }
        } else {
          console.log('🔔 CardStore: Skipping completion notification - conditions not met:', {
            statusIsDone: updates.status === 'done',
            hasAssignee: !!cardToCheck.assigneeId,
            hasCreator: !!cardToCheck.createdById,
            isDraft: oldCard.is_draft,
            assigneeId: cardToCheck.assigneeId,
            createdById: cardToCheck.createdById
          });
        }
        
        // Generic status change event (for feed history)
        // December 9, 2025: Skip for draft cards
        if (!oldCard.is_draft) {
          useFeedStore.getState().addEvent({
            type: feedEventType(feedKindForCardType(updatedCard.type) === 'task' ? 'task_updated' : 'idea_updated'),
            title: 'Status Changed',
            description: `${currentUser.name} moved "${updatedCard.title}" from ${oldStatusName} to ${newStatusName}`,
            userId: currentUser.id,
            initiatorId: currentUser.id,
            cardId: updatedCard.id,
            sprintId: updatedCard.sprintId,
            roadmapId: updatedCard.roadmapId,
          });
        }

        // Enter Review (or Done skipping Review) → ping pending approvers. List edits stay quiet.
        if (!oldCard.is_draft && updates.status) {
          notifyApproversReviewGate({
            card: {
              ...updatedCard,
              approvalWorkflow: updatedCard.approvalWorkflow || oldCard.approvalWorkflow,
            },
            oldStatus: oldCard.status,
            newStatus: updates.status,
            actorId: currentUser.id,
            actorName: currentUser.name,
          });
        }
      }

      // Assignment change tracking (December 4, 2025: Fixed to work for ideas too, exclude self-assignment)
      // December 9, 2025: Skip assignment notifications for draft cards
      if (updates.assigneeId !== undefined && updates.assigneeId !== oldCard.assigneeId) {
        console.log('CardStore: Assignment change detected!', {
          oldAssigneeId: oldCard.assigneeId,
          newAssigneeId: updates.assigneeId,
          cardId: updatedCard.id,
          cardTitle: updatedCard.title,
          cardType: updatedCard.type,
          isDraft: updatedCard.is_draft
        });
        
        // Skip notification if card is still a draft
        if (!updatedCard.is_draft) {
          const assigneeUser = useUserStore.getState().allUsers.find(u => u.id === updates.assigneeId);
          const oldAssigneeUser = oldCard.assigneeId ? useUserStore.getState().allUsers.find(u => u.id === oldCard.assigneeId) : null;
          const assigneeName = assigneeUser ? assigneeUser.name : 'Unassigned';
          const oldAssigneeName = oldAssigneeUser ? oldAssigneeUser.name : 'Unassigned';
          const typeLabel = cardTypeLabel(updatedCard.type);

          let description = `${currentUser.name} reassigned ${typeLabel} "${updatedCard.title}" from ${oldAssigneeName} to ${assigneeName}`;
          if (!oldCard.assigneeId) {
            description = `${currentUser.name} assigned ${typeLabel} "${updatedCard.title}" to ${assigneeName}`;
          } else if (!updates.assigneeId) {
            description = `${currentUser.name} unassigned ${typeLabel} "${updatedCard.title}" from ${oldAssigneeName}`;
          }

          console.log('CardStore: Creating assignment event:', description);

          // Only notify the new assignee if it's not self-assignment
          const shouldNotifyAssignee = updates.assigneeId && updates.assigneeId !== currentUser.id;
          
          useFeedStore.getState().addEvent({
            type: feedEventType(feedKindForCardType(updatedCard.type) === 'task' ? 'task_updated' : 'idea_updated'),
            title: shouldNotifyAssignee ? `${typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)} Assigned` : 'Assignment Changed',
            description: description,
            // assigneeId is string|null; FeedEvent.userId is string|undefined
            userId: shouldNotifyAssignee ? (updates.assigneeId ?? undefined) : currentUser.id,
            initiatorId: currentUser.id,
            cardId: updatedCard.id,
            sprintId: updatedCard.sprintId,
            roadmapId: updatedCard.roadmapId,
          });

          maybeWakeTeamBotsOnAssign(updatedCard, updates.assigneeId);
        } else {
          console.log('CardStore: Skipping assignment notification for draft card');
        }
      } else {
        console.log('CardStore: No assignment change detected', {
          assigneeIdDefined: updates.assigneeId !== undefined,
          oldAssigneeId: oldCard.assigneeId,
          newAssigneeId: updates.assigneeId,
          areEqual: updates.assigneeId === oldCard.assigneeId
        });
      }

      // Draft finalization: notify/wake when assignee was already on the draft and this
      // write only clears is_draft. Skip when the same update also changes assignee —
      // Assign (save+finalize) already handled feed + TeamBots wake above, and a second
      // wake opens a duplicate OpenClaw session.
      const assigneeChangedInThisUpdate =
        updates.assigneeId !== undefined && updates.assigneeId !== oldCard.assigneeId;
      if (
        oldCard.is_draft &&
        updates.is_draft === false &&
        updatedCard.assigneeId &&
        updatedCard.assigneeId !== currentUser.id &&
        !assigneeChangedInThisUpdate
      ) {
        console.log('CardStore: Draft finalized with assignee, sending notification', {
          assigneeId: updatedCard.assigneeId,
          cardTitle: updatedCard.title,
          cardType: updatedCard.type
        });
        
        const assigneeUser = useUserStore.getState().allUsers.find(u => u.id === updatedCard.assigneeId);
        const assigneeName = assigneeUser ? assigneeUser.name : 'Former User';
        const typeLabel = cardTypeLabel(updatedCard.type);
        const eventType = feedEventType(feedKindForCardType(updatedCard.type) === 'task' ? 'task_updated' : 'idea_updated');
        
        console.log('🔔 [cardStore] Sending draft finalization notification:', {
          eventType,
          assigneeId: updatedCard.assigneeId,
          assigneeName,
          cardTitle: updatedCard.title,
          cardType: updatedCard.type,
        });
        
        useFeedStore.getState().addEvent({
          type: eventType,
          title: `${typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)} Assigned`,
          description: `${currentUser.name} assigned ${typeLabel} "${updatedCard.title}" to ${assigneeName}`,
          userId: updatedCard.assigneeId, // Notify the assignee
          initiatorId: currentUser.id,
          cardId: updatedCard.id,
          sprintId: updatedCard.sprintId,
          roadmapId: updatedCard.roadmapId,
        });

        maybeWakeTeamBotsOnAssign(updatedCard, updatedCard.assigneeId);
      }

      // Draft → live while already in Review: ping approvers set up on create.
      if (
        oldCard.is_draft
        && updates.is_draft === false
        && updatedCard.status === 'under_review'
      ) {
        const pending = (updatedCard.approvalWorkflow || []).filter(
          (a) => a.status === 'pending' && a.userId !== currentUser.id,
        );
        for (const approver of pending) {
          useFeedStore.getState().addEvent({
            type: 'approval_requested',
            title: 'Ready for your review',
            description: `${currentUser.name} assigned "${updatedCard.title}" for review`,
            userId: approver.userId,
            initiatorId: currentUser.id,
            cardId: updatedCard.id,
            sprintId: updatedCard.sprintId,
            roadmapId: updatedCard.roadmapId,
          });
        }
      }

      // Priority change tracking
      // December 9, 2025: Skip for drafts, support both tasks and ideas
      if (updates.priority !== undefined && updates.priority !== oldCard.priority) {
        if (!oldCard.is_draft) {
          const priorityDisplayNames = {
            'low': 'Low',
            'medium': 'Medium',
            'high': 'High'
          };
          
          const oldPriorityName = priorityDisplayNames[oldCard.priority as keyof typeof priorityDisplayNames] || oldCard.priority;
          const newPriorityName = priorityDisplayNames[updates.priority as keyof typeof priorityDisplayNames] || updates.priority;
          
          useFeedStore.getState().addEvent({
            type: feedEventType(feedKindForCardType(updatedCard.type) === 'task' ? 'task_updated' : 'idea_updated'),
            title: 'Priority Changed',
            description: `${currentUser.name} changed priority of "${updatedCard.title}" from ${oldPriorityName} to ${newPriorityName}`,
            userId: currentUser.id,
            initiatorId: currentUser.id,
            cardId: updatedCard.id,
            sprintId: updatedCard.sprintId,
            roadmapId: updatedCard.roadmapId,
          });
        }
      }

      // Due date change tracking
      // December 9, 2025: Skip for drafts, support both tasks and ideas
      if (updates.dueDate !== undefined) {
        const oldDueDate = oldCard.dueDate ? new Date(oldCard.dueDate).toLocaleDateString() : 'No due date';
        const newDueDate = updates.dueDate ? new Date(updates.dueDate).toLocaleDateString() : 'No due date';
        
        if (oldDueDate !== newDueDate && !oldCard.is_draft) {
          let description = `${currentUser.name} changed due date of "${updatedCard.title}" from ${oldDueDate} to ${newDueDate}`;
          if (!oldCard.dueDate) {
            description = `${currentUser.name} set due date of "${updatedCard.title}" to ${newDueDate}`;
          } else if (!updates.dueDate) {
            description = `${currentUser.name} removed due date from "${updatedCard.title}"`;
          }
          
          useFeedStore.getState().addEvent({
            type: feedEventType(feedKindForCardType(updatedCard.type) === 'task' ? 'task_updated' : 'idea_updated'),
            title: 'Due Date Changed',
            description: description,
            userId: currentUser.id,
            initiatorId: currentUser.id,
            cardId: updatedCard.id,
            sprintId: updatedCard.sprintId,
            roadmapId: updatedCard.roadmapId,
          });
        }
      }

      // Project change tracking (for ticket number changes)
      if (updates.projectId !== undefined && updates.projectId !== oldCard.projectId) {
        const { projects } = (await import('./projectStore')).default.getState();
        const oldProject = oldCard.projectId ? projects.find(p => p.id === oldCard.projectId) : null;
        const newProject = updates.projectId ? projects.find(p => p.id === updates.projectId) : null;
        
        const oldTicketId = oldCard.ticketNumber && oldProject ? 
          `${oldProject.projectKey}-${oldCard.ticketNumber}` : null;
        const newTicketId = updatedCard.ticketNumber && newProject ?
          `${newProject.projectKey}-${updatedCard.ticketNumber}` : null;
        
        let description = '';
        if (oldProject && newProject) {
          // Moved between projects
          description = `${currentUser.name} moved "${updatedCard.title}" from ${oldProject.name} to ${newProject.name}`;
          if (oldTicketId && newTicketId) {
            description += ` (${oldTicketId} → ${newTicketId})`;
          }
        } else if (newProject) {
          // Added to project
          description = `${currentUser.name} added "${updatedCard.title}" to ${newProject.name}`;
          if (newTicketId) {
            description += ` (${newTicketId})`;
          }
        } else if (oldProject) {
          // Removed from project
          description = `${currentUser.name} removed "${updatedCard.title}" from ${oldProject.name}`;
          if (oldTicketId) {
            description += ` (was ${oldTicketId})`;
          }
        }
        
        // December 9, 2025: Skip for drafts, support both tasks and ideas
        if (description && !oldCard.is_draft) {
          useFeedStore.getState().addEvent({
            type: feedEventType(feedKindForCardType(updatedCard.type) === 'task' ? 'task_updated' : 'idea_updated'),
            title: 'Project Changed',
            description,
            userId: currentUser.id,
            initiatorId: currentUser.id,
            cardId: updatedCard.id,
            sprintId: updatedCard.sprintId,
            roadmapId: updatedCard.roadmapId,
          });
        }
      }

      // Blocking/unblocking tracking
      if (updates.blocked !== undefined && updates.blocked !== oldCard.blocked) {
        const blockedReason = updates.blockedReason || oldCard.blockedReason || 'No reason provided';
        
        if (updates.blocked) {
          useFeedStore.getState().addEvent({
            type: 'task_blocked',
            title: 'Task Blocked',
            description: `${currentUser.name} blocked "${updatedCard.title}". Reason: ${blockedReason}`,
            userId: currentUser.id,
            initiatorId: currentUser.id,
            cardId: updatedCard.id,
            sprintId: updatedCard.sprintId,
            roadmapId: updatedCard.roadmapId,
          });
        } else {
          useFeedStore.getState().addEvent({
            type: 'task_unblocked',
            title: 'Task Unblocked',
            description: `${currentUser.name} unblocked "${updatedCard.title}"`,
            userId: currentUser.id,
            initiatorId: currentUser.id,
            cardId: updatedCard.id,
            sprintId: updatedCard.sprintId,
            roadmapId: updatedCard.roadmapId,
          });
        }
      }

      // Approval workflow: list edits are quiet. Ping only when their turn starts
      // (enter Review, or late-add while already in Review). Approve/reject still notify the owner.
      if (
        updates.approvalWorkflow !== undefined
        && updates.approvalWorkflow !== oldCard.approvalWorkflow
        && !oldCard.is_draft
        && !updatedCard.is_draft
      ) {
        const oldApprovers = oldCard.approvalWorkflow || [];
        const newApprovers = updates.approvalWorkflow || [];
        const { added: addedApprovers, statusChanges } =
          diffApprovalWorkflow(oldApprovers, newApprovers);

        // Already in Review and someone new is added → their turn starts now.
        if (updatedCard.status === 'under_review') {
          for (const approver of addedApprovers) {
            if (approver.status !== 'pending' || approver.userId === currentUser.id) continue;
            useFeedStore.getState().addEvent({
              type: 'approval_requested',
              title: 'Ready for your review',
              description: `${currentUser.name} asked you to review "${updatedCard.title}"`,
              userId: approver.userId,
              initiatorId: currentUser.id,
              cardId: updatedCard.id,
              sprintId: updatedCard.sprintId,
              roadmapId: updatedCard.roadmapId,
            });
          }
        }

        for (const approver of statusChanges) {
          const approverUser = useUserStore.getState().allUsers.find(u => u.id === approver.userId);
          const approverName = approverUser ? approverUser.name : 'Former User';
          
          // Notify the assignee (or creator) — the people waiting on the gate.
          const gateOwnerId =
            updatedCard.assigneeId || updatedCard.createdById || currentUser.id;
          if (approver.status === 'approved') {
            useFeedStore.getState().addEvent({
              type: 'approval_approved',
              title: 'Approved',
              description: `${approverName} approved "${updatedCard.title}"`,
              userId: gateOwnerId,
              initiatorId: approver.userId,
              cardId: updatedCard.id,
              sprintId: updatedCard.sprintId,
              roadmapId: updatedCard.roadmapId,
            });
          } else if (approver.status === 'rejected') {
            useFeedStore.getState().addEvent({
              type: 'approval_rejected',
              title: 'Changes requested',
              description: `${approverName} rejected "${updatedCard.title}"${approver.reason ? `: ${approver.reason}` : ''}`,
              userId: gateOwnerId,
              initiatorId: approver.userId,
              cardId: updatedCard.id,
              sprintId: updatedCard.sprintId,
              roadmapId: updatedCard.roadmapId,
            });
          }
        }
      }

      // Event Trigger for Blocked Tasks (proactive agent)
      if (updates.blocked === true && oldCard.blocked === false) {
        console.log(`Card ${updatedCard.title} was just blocked. Triggering agent.`);
        const allUsers = useUserStore.getState().allUsers;
        const projectManagerAgent = allUsers.find((u) => u.isAgent && u.agentType === 'Project Manager');

        if (projectManagerAgent) {
          const targetStreamType = updatedCard.teamId ? 'team_stream' : 'company_stream';
          const targetRecipientId = updatedCard.teamId || updatedCard.companyId;

          if (targetRecipientId) {
            const proactivePrompt = `A task "${updatedCard.title}" (ID: ${updatedCard.id}) has just been marked as BLOCKED by ${currentUser.name}. It is currently in the "${updatedCard.status}" column. Please review the situation and suggest next steps or actions.`;

            supabase.functions
              .invoke('agent-orchestrator', {
                body: {
                  // BUG (pre-existing): this referenced undefined `agileCoachAgent`, so the
                  // blocked-task agent invocation always threw and was swallowed by the outer
                  // catch. The found agent is `projectManagerAgent`; using it as clearly intended.
                  agent_id: projectManagerAgent.id,
                  sender_id: currentUser.id,
                  company_id: currentUser.companyId,
                  proactive_prompt: proactivePrompt,
                  response_stream_type: targetStreamType,
                  response_recipient_id: targetRecipientId,
                },
              })
              .then(({ data, error }) => {
                if (error) {
                  console.error('Error invoking agent-orchestrator for blocked task:', error);
                } else {
                  console.log('Agent-orchestrator invoked successfully for blocked task:', data);
                }
              })
              .catch((err) => {
                console.error('Unhandled promise rejection from agent-orchestrator invocation:', err);
              });
          } else {
            console.warn('Could not determine target stream for blocked task notification (no teamId or companyId).');
          }
        } else {
          console.warn('No "Project Manager" agent found to notify about blocked task.');
        }
      }

      // Final state update - only update _allCards if we haven't already done so above
      // (We already updated both _allCards and cards above when skipFilter was handled)
      if (!skipFilter) {
        console.log('📝 [cardStore] updateCard - Final state update (skipFilter=false), calling _applyFilter');
        set((state) => ({
          _allCards: state._allCards.map((card) => (card.id === cardId ? updatedCard : card)),
          isLoading: false,
        }));
        get()._applyFilter();
      } else {
        console.log('📝 [cardStore] updateCard - Final state update (skipFilter=true), skipping _applyFilter');
        // When skipFilter is true, we already updated both arrays above, just set isLoading
        set({ isLoading: false });
      }
    } catch (error: unknown) {
      console.error('Error updating card:', error);
      set({ error: `Failed to update card: ${errorMessage(error)}`, isLoading: false });
    }
  },

  moveCard: async (cardId, updates) => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      const card = get()._allCards.find((c) => c.id === cardId);
      if (!card) return;

      // Cast: sprintId/roadmapId here allow `null` (= clear the field, passed through to the
      // DB by mapCardUpdatesToDB), but Card declares them as `string | undefined` only.
      const dbUpdates = mapCardUpdatesToDB({
        ...updates,
        lastActivity: {
          type: 'status_change',
          timestamp: new Date().toISOString(),
          userId: currentUser.id,
          details: `Card moved${updates.status ? ` to ${updates.status}` : ''}`,
        },
      } as Partial<Card>);

      // Auto-stop time tracking when moved to "done"
      if (updates.status === 'done' && isWorkCardType(card.type)) {
        const now = new Date().toISOString();
        
        // If time tracking was active (started or paused), calculate and save total time
        if (card.startedAt || card.pausedAt || card.lastWorkSessionStart) {
          let newTotalSeconds = card.totalWorkSeconds || 0;
          
          if (card.lastWorkSessionStart) {
            const sessionStart = new Date(card.lastWorkSessionStart).getTime();
            const sessionSeconds = Math.floor((Date.now() - sessionStart) / 1000);
            // Cap accrue on done so a forgotten session cannot dump days into totals
            const MAX_SESSION_ACCRUE = 12 * 60 * 60;
            newTotalSeconds += Math.min(Math.max(0, sessionSeconds), MAX_SESSION_ACCRUE);
          }
          
          dbUpdates.started_at = null;
          dbUpdates.last_work_session_start = null;
          dbUpdates.paused_at = null;
          dbUpdates.total_work_seconds = newTotalSeconds;
          dbUpdates.ended_at = now;
        } else {
          // For cards without explicit time tracking, set ended_at
          // If card moved directly from "todo" to "done", started_at will be null (which is fine)
          dbUpdates.ended_at = now;
        }
      }

      // When moving to "done", always set position to 0 (top of list)
      if (updates.status === 'done') {
        dbUpdates.position = 0;
        
        // Shift all other done cards down by 1
        const doneCards = get()._allCards
          .filter(c => c.status === 'done' && c.id !== cardId)
          .sort((a, b) => (a.position || 0) - (b.position || 0));
        
        // Update positions for all done cards (shift them down)
        const positionUpdates = doneCards.map((c, idx) => ({ id: c.id, position: idx + 1 }));
        if (positionUpdates.length > 0) {
          // Update positions in background (non-blocking)
          updateCardPositions(positionUpdates).catch(err => 
            console.error('Error updating done card positions:', err)
          );
        }
      }

      const updatedCard = await updateCardById(cardId, dbUpdates);

      useFeedStore.getState().addEvent({
        type: 'task_updated',
        title: 'Card Moved',
        description: `${updatedCard.title} moved to ${updates.status || 'another column'} by ${currentUser.name}`,
        userId: currentUser.id,
        initiatorId: currentUser.id,
        cardId: updatedCard.id,
        sprintId: updatedCard.sprintId,
        roadmapId: updatedCard.roadmapId,
      });

      if (updates.status && !card.is_draft) {
        notifyApproversReviewGate({
          card: {
            ...updatedCard,
            approvalWorkflow: updatedCard.approvalWorkflow || card.approvalWorkflow,
          },
          oldStatus: card.status,
          newStatus: updates.status,
          actorId: currentUser.id,
          actorName: currentUser.name,
        });
      }

      set((state) => ({
        _allCards: state._allCards.map((c) => (c.id === cardId ? updatedCard : c)),
        isLoading: false,
      }));
      
      // Only apply filter if not in drag operation
      if (!get().isDragInProgress) {
        get()._applyFilter();
      }
    } catch (error: unknown) {
      console.error('Error moving card:', error);
      set({ error: `Failed to move card: ${errorMessage(error)}`, isLoading: false });
    }
  },

  // Optimistic move: Updates UI immediately, then syncs with DB
  // Optionally emits a time_tracking feed event for History tab (action: start | pause | resume | stop)
  updateTimeTrackingOptimistic: async (cardId: string, updates: Partial<Card>, timeTrackingAction?: { action: 'start' | 'pause' | 'resume' | 'stop'; totalSecondsAfter?: number }) => {
    // Optimistic update for time tracking (like moveCardOptimistic)
    // Updates store immediately, then syncs with database in background
    
    const card = get()._allCards.find((c) => c.id === cardId);
    if (!card) return;

    // 1. OPTIMISTIC UPDATE (immediate, no database call)
    const optimisticCard = { ...card, ...updates };
    
    set((state) => {
      const updatedAllCards = state._allCards.map((c) => (c.id === cardId ? optimisticCard : c));
      
      // Update cards array directly (apply team filter) without calling _applyFilter
      const updatedCards = updatedAllCards;
      
      return { _allCards: updatedAllCards, cards: updatedCards };
    });

    // 2. DATABASE SYNC (async, in background)
    try {
      const dbUpdates = mapCardUpdatesToDB(updates);
      await updateCardById(cardId, dbUpdates);
      
      // Update with actual DB response (in case of any differences)
      const updatedCard = await fetchCardById(cardId);
      if (updatedCard) {
        set((state) => {
          const updatedAllCards = state._allCards.map((c) => (c.id === cardId ? updatedCard : c));
          
          // Update cards array directly
          const updatedCards = updatedAllCards;
          
          return { _allCards: updatedAllCards, cards: updatedCards };
        });

        // 3. Emit time_tracking feed event for History tab (start/pause/resume/stop)
        if (timeTrackingAction) {
          const currentUser = useUserStore.getState().currentUser;
          const totalSec = timeTrackingAction.totalSecondsAfter ?? updatedCard.totalWorkSeconds ?? 0;
          const titles: Record<string, string> = { start: 'Time tracking started', pause: 'Time tracking paused', resume: 'Time tracking resumed', stop: 'Time tracking stopped' };
          const desc = totalSec > 0
            ? `${titles[timeTrackingAction.action]} by ${currentUser?.name ?? 'Someone'}. Total time: ${formatTimeForHistory(totalSec)}`
            : `${titles[timeTrackingAction.action]} by ${currentUser?.name ?? 'Someone'}`;
          useFeedStore.getState().addEvent({
            type: 'time_tracking',
            title: titles[timeTrackingAction.action],
            description: desc,
            userId: currentUser?.id ?? undefined,
            initiatorId: currentUser?.id ?? undefined,
            cardId: updatedCard.id,
            sprintId: updatedCard.sprintId,
            roadmapId: updatedCard.roadmapId,
            metadata: { action: timeTrackingAction.action, totalSecondsAfter: timeTrackingAction.totalSecondsAfter ?? totalSec, assigneeId: updatedCard.assigneeId },
          });
        }
      }
    } catch (error: unknown) {
      console.error('Error syncing time tracking update:', error);
      // Revert optimistic update on error
      const originalCard = await fetchCardById(cardId);
      if (originalCard) {
        set((state) => {
          const updatedAllCards = state._allCards.map((c) => (c.id === cardId ? originalCard : c));
          
          const updatedCards = updatedAllCards;
          
          return { _allCards: updatedAllCards, cards: updatedCards };
        });
      }
    }
  },

  moveCardOptimistic: async (cardId, targetStatus, targetIndex) => {
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      const card = get()._allCards.find((c) => c.id === cardId);
      if (!card) return;

      const originalStatus = card.status;
      const sortedCards = [...get()._allCards].sort((a, b) => (a.position || 0) - (b.position || 0));

      // 1. OPTIMISTIC UI UPDATE (instant, no flicker)
      console.log('🚀 moveCardOptimistic: Applying optimistic update');
      
      const sourceCards = sortedCards.filter(c => c.status === originalStatus && c.id !== cardId);
      const targetCards = sortedCards.filter(c => c.status === targetStatus);
      
      // When moving to "done", always insert at position 0 (top of list)
      const finalTargetIndex = targetStatus === 'done' ? 0 : targetIndex;
      
      // Insert card at target position
      const newTargetCards = [...targetCards];
      newTargetCards.splice(finalTargetIndex, 0, { ...card, status: targetStatus });

      // Build complete updated card list with new positions
      const optimisticCards = sortedCards.map(c => {
        if (c.id === cardId) {
          // The moved card gets new status and position (always 0 for "done")
          return { ...c, status: targetStatus, position: finalTargetIndex };
        } else if (c.status === originalStatus) {
          // Source column: reindex remaining cards
          const newIndex = sourceCards.findIndex(sc => sc.id === c.id);
          return { ...c, position: newIndex };
        } else if (c.status === targetStatus) {
          // Target column: reindex with inserted card
          const newIndex = newTargetCards.findIndex(tc => tc.id === c.id);
          return { ...c, position: newIndex };
        }
        return c;
      });

      // Apply optimistic update immediately
      set({ _allCards: optimisticCards });
      
      // Update cards array directly to reflect the change in UI
      // For "done" moves, update cards without full filter to prevent redraws
      if (targetStatus === 'done' || originalStatus === 'done') {
        // Update cards array directly by applying the same filter logic
        const filtered = optimisticCards;
        
        set({ cards: filtered });
      } else {
        // For other moves, use the full filter
        get()._applyFilter();
      }

      // 2. DATABASE UPDATE (async, in background)
      console.log('💾 moveCardOptimistic: Syncing with database');
      
      // Prepare updates object
      const updates: Partial<Card> = {
        status: targetStatus,
        lastActivity: {
          type: 'status_change',
          timestamp: new Date().toISOString(),
          userId: currentUser.id,
          details: `Card moved to ${targetStatus}`,
        },
      };

      // Do not auto-start / auto-resume the clock on column move — VCR Play owns that.
      // Reopening from done only clears endedAt; user hits Play to track again.
      if (targetStatus === 'inProgress' && isWorkCardType(card.type) && card.status === 'done') {
        Object.assign(updates, timeFieldsOnReopenFromDone());
      }

      // Auto-stop time tracking when moved to "done"
      let committedTotalSeconds: number | undefined;
      if (targetStatus === 'done' && isWorkCardType(card.type)) {
        const now = new Date().toISOString();
        const stopped = timeFieldsOnMoveToDone(card, now);
        Object.assign(updates, stopped.updates);
        committedTotalSeconds = stopped.committedTotalSeconds;
      }

      const dbUpdates = mapCardUpdatesToDB(updates);
      
      // When moving to "done", always set position to 0 in DB
      if (targetStatus === 'done') {
        dbUpdates.position = 0;
      }
      
      await updateCardById(cardId, dbUpdates);

      // Update positions for both columns in parallel
      const positionUpdates = [
        ...sourceCards.map((c, idx) => ({ id: c.id, position: idx })),
        ...newTargetCards.map((c, idx) => ({ id: c.id, position: idx }))
      ];

      await updateCardPositions(positionUpdates);

      // 4. If task is marked as done and was assigned to someone else, notify the creator
      if (targetStatus === 'done' && card.assigneeId && card.createdById) {
        console.log('🔔 moveCardOptimistic: Task marked as done, checking if notification needed:', {
          cardId: card.id,
          cardTitle: card.title,
          assigneeId: card.assigneeId,
          createdById: card.createdById,
          areDifferent: card.createdById !== card.assigneeId
        });
        
        // Only notify if creator is different from assignee (not self-assignment)
        if (card.createdById !== card.assigneeId) {
          const assigneeUser = useUserStore.getState().allUsers.find(u => u.id === card.assigneeId);
          const assigneeName = assigneeUser ? assigneeUser.name : 'Someone';
          
          console.log('🔔 moveCardOptimistic: Creating completion notification for creator:', {
            creatorId: card.createdById,
            assigneeName: assigneeName,
            cardTitle: card.title
          });
          
          useFeedStore.getState().addEvent({
            type: feedEventType('task_completed_by_assignee'),
            title: 'Task Completed',
            description: `${assigneeName} completed "${card.title}"`,
            userId: card.createdById, // Notify the creator
            initiatorId: currentUser.id, // Person who marked it done (usually the assignee)
            cardId: card.id,
            sprintId: card.sprintId,
            roadmapId: card.roadmapId,
          });
        } else {
          console.log('🔔 moveCardOptimistic: Skipping notification - self-assignment (creator === assignee)');
        }
      }

      // 3. Create feed event (generic status change)
      useFeedStore.getState().addEvent({
        type: 'task_updated',
        title: 'Card Moved',
        description: `${card.title} moved to ${targetStatus} by ${currentUser.name}`,
        userId: currentUser.id,
        initiatorId: currentUser.id,
        cardId: card.id,
        sprintId: card.sprintId,
        roadmapId: card.roadmapId,
      });

      // Drag In Progress → Review (and Done-skip-Review) must ping approvers too.
      if (!card.is_draft) {
        notifyApproversReviewGate({
          card,
          oldStatus: originalStatus,
          newStatus: targetStatus,
          actorId: currentUser.id,
          actorName: currentUser.name,
        });
      }

      // 3b. If we committed time when moving to done, add time_tracking event for History
      if (targetStatus === 'done' && isWorkCardType(card.type) && committedTotalSeconds != null && committedTotalSeconds > 0) {
        useFeedStore.getState().addEvent({
          type: 'time_tracking',
          title: 'Time tracking stopped',
          description: `Time tracking stopped by ${currentUser.name}. Total time: ${formatTimeForHistory(committedTotalSeconds)}`,
          userId: currentUser.id,
          initiatorId: currentUser.id,
          cardId: card.id,
          sprintId: card.sprintId,
          roadmapId: card.roadmapId,
          metadata: { action: 'stop', totalSecondsAfter: committedTotalSeconds, assigneeId: card.assigneeId },
        });
      }

      console.log('✅ moveCardOptimistic: Complete');
    } catch (error: unknown) {
      console.error('❌ Error in moveCardOptimistic:', error);
      // On error, refresh from DB to get correct state
      get().initializeCards();
    }
  },

  toggleCardBlocked: async (cardId, reason) => {
    const card = get()._allCards.find((c) => c.id === cardId);
    if (card) {
      const newBlockedStatus = !card.blocked;
      const updates: Partial<Card> = { blocked: newBlockedStatus };
      if (newBlockedStatus) {
        updates.blockedReason = reason || 'No reason provided.';
        updates.blockedAt = new Date().toISOString();
      } else {
        // ✅ Clear fields in DB by sending `null`
        updates.blockedReason = null;
        updates.blockedAt = null;
      }
      await get().updateCard(card.id, updates);
    }
  },

  deleteCard: async (cardId) => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      const card = get()._allCards.find((c) => c.id === cardId);
      if (!card) throw new Error('Card not found');

      useFeedStore.getState().addEvent({
        type: feedEventType(feedKindForCardType(card.type) === 'task' ? 'task_deleted' : 'idea_deleted'),
        title: 'Card Deleted',
        description: `${currentUser.name} deleted "${card.title}"`,
        userId: currentUser.id,
        initiatorId: currentUser.id,
        cardId: card.id,
        sprintId: card.sprintId,
        roadmapId: card.roadmapId,
      });

      await deleteCardById(cardId);

      set((state) => ({
        _allCards: state._allCards.filter((c) => c.id !== cardId),
        isLoading: false,
      }));
      get()._applyFilter();
    } catch (error: unknown) {
      console.error('Error deleting card:', error);
      set({ error: `Failed to delete card: ${errorMessage(error)}`, isLoading: false });
    }
  },

  voteOnCard: async (cardId, userId, voteType) => {
    try {
      const card = get()._allCards.find((c) => c.id === cardId);
      if (!card) return;

      const newVotes = { ...card.sentimentVotes };
      const userVotes = [...newVotes.user_votes];

      const existingVoteIndex = userVotes.findIndex((vote) => vote.user_id === userId);
      const existingVote = existingVoteIndex >= 0 ? userVotes[existingVoteIndex] : null;

      let voteAction: 'new' | 'change' | 'unvote' = 'new';
      let previousVoteType: 'up' | 'down' | null = null;

      if (existingVote) {
        previousVoteType = existingVote.type;
        if (existingVote.type === voteType) {
          voteAction = 'unvote';
          newVotes[voteType]--;
          userVotes.splice(existingVoteIndex, 1);
        } else {
          voteAction = 'change';
          newVotes[existingVote.type]--;
          newVotes[voteType]++;
          userVotes[existingVoteIndex] = { user_id: userId, type: voteType };
        }
      } else {
        voteAction = 'new';
        newVotes[voteType]++;
        userVotes.push({ user_id: userId, type: voteType });
      }

      newVotes.user_votes = userVotes;

      // Optimistic update on _allCards
      set((state) => ({
        _allCards: state._allCards.map((c) => (c.id === cardId ? { ...c, sentimentVotes: newVotes } : c)),
      }));
      get()._applyFilter();

      const dbUpdates = mapCardUpdatesToDB({ sentimentVotes: newVotes });
      const updatedCard = await updateCardById(cardId, dbUpdates);

      const currentUser = useUserStore.getState().currentUser;
      if (currentUser) {
        let eventType: 'vote_up' | 'vote_down' | 'vote_changed' | 'vote_unvoted';
        let description: string;

        switch (voteAction) {
          case 'new':
            eventType = voteType === 'up' ? 'vote_up' : 'vote_down';
            description = `${currentUser.name} voted ${voteType} on "${card.title}"`;
            break;
          case 'change':
            eventType = 'vote_changed';
            description = `${currentUser.name} changed their vote from ${previousVoteType} to ${voteType} on "${card.title}"`;
            break;
          case 'unvote':
            eventType = 'vote_unvoted';
            description = `${currentUser.name} removed their ${voteType} vote on "${card.title}"`;
            break;
        }

        useFeedStore.getState().addEvent({
          type: eventType,
          title: `Card Vote ${voteAction === 'new' ? 'Added' : voteAction === 'change' ? 'Changed' : 'Removed'}`,
          description,
          userId: currentUser.id,
          initiatorId: currentUser.id,
          cardId: updatedCard.id,
          sprintId: updatedCard.sprintId,
          roadmapId: updatedCard.roadmapId,
        });

        if (card.createdById && card.createdById !== userId) {
          useFeedStore.getState().addEvent({
            type: eventType,
            title: `Vote on Your ${cardTypeTitle(card.type)}`,
            description,
            userId: card.createdById,
            initiatorId: currentUser.id,
            cardId: updatedCard.id,
            sprintId: updatedCard.sprintId,
            roadmapId: updatedCard.roadmapId,
          });
        }

        if (card.assigneeId && card.assigneeId !== userId && card.assigneeId !== card.createdById) {
          useFeedStore.getState().addEvent({
            type: eventType,
            title: `Vote on Assigned ${cardTypeTitle(card.type)}`,
            description,
            userId: card.assigneeId,
            initiatorId: currentUser.id,
            cardId: updatedCard.id,
            sprintId: updatedCard.sprintId,
            roadmapId: updatedCard.roadmapId,
          });
        }

        const skipVoteNotify = [userId, card.createdById, card.assigneeId];
        try {
          await useFollowStore.getState().notifyFollowers(
            followRefsFromCard(updatedCard),
            skipVoteNotify,
            {
              type: eventType,
              title: `Vote on Followed ${cardTypeTitle(card.type)}`,
              description,
              initiatorId: currentUser.id,
              cardId: updatedCard.id,
              sprintId: updatedCard.sprintId,
              roadmapId: updatedCard.roadmapId,
              projectId: updatedCard.projectId ?? undefined,
            },
          );
        } catch (followErr) {
          console.warn('Follower notify on vote failed (non-critical):', followErr);
        }
      }

      // Ensure local state is consistent with DB after async operation
      set((state) => ({
        _allCards: state._allCards.map((c) => (c.id === cardId ? updatedCard : c)),
      }));
      get()._applyFilter();
    } catch (error: unknown) {
      console.error('Error voting on card:', error);
      set({ error: `Failed to vote on card: ${errorMessage(error)}` });
    }
  },

  convertCardType: async (cardId, newType) => {
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      // Get old card to check if it was a draft
      const oldCard = get()._allCards.find((c) => c.id === cardId);
      if (!oldCard) throw new Error('Card not found');

      // Get the mapped card directly from the update (no extra fetch needed)
      const updatedCard = await rpcConvertCardType(cardId, newType, currentUser.id);

      // Update _allCards with the converted card
      set((state) => ({
        _allCards: state._allCards.map((card) => 
          card.id === cardId ? updatedCard : card
        ),
      }));
      
      // Re-apply filter to update the cards view
      get()._applyFilter();

      useFeedStore.getState().addEvent({
        type: 'card_converted',
        title: 'Card Converted',
        description: `${currentUser.name} converted "${updatedCard.title}" to ${newType}`,
        userId: currentUser.id,
        initiatorId: currentUser.id,
        cardId: updatedCard.id,
        sprintId: updatedCard.sprintId,
        roadmapId: updatedCard.roadmapId,
      });
      
      // December 9, 2025: If converting a draft with an assignee, send assignment notification
      // This makes "Convert to Task" work like "Assign" for draft ideas
      if (oldCard.is_draft && updatedCard.assigneeId && updatedCard.assigneeId !== currentUser.id) {
        console.log('CardStore: Draft converted with assignee, sending notification', {
          assigneeId: updatedCard.assigneeId,
          cardTitle: updatedCard.title,
          oldType: oldCard.type,
          newType: updatedCard.type,
        });
        
        const assigneeUser = useUserStore.getState().allUsers.find(u => u.id === updatedCard.assigneeId);
        const assigneeName = assigneeUser ? assigneeUser.name : 'Former User';
        const typeLabel = cardTypeLabel(updatedCard.type);
        
        useFeedStore.getState().addEvent({
          type: feedEventType(feedKindForCardType(updatedCard.type) === 'task' ? 'task_updated' : 'idea_updated'),
          title: `${typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)} Assigned`,
          description: `${currentUser.name} assigned ${typeLabel} "${updatedCard.title}" to ${assigneeName}`,
          userId: updatedCard.assigneeId, // Notify the assignee
          initiatorId: currentUser.id,
          cardId: updatedCard.id,
          sprintId: updatedCard.sprintId,
          roadmapId: updatedCard.roadmapId,
        });
      }
    } catch (error: unknown) {
      console.error('Error converting card:', error);
      set({ error: `Failed to convert card: ${errorMessage(error)}` });
    }
  },

  addDependency: async (cardId, dependencyId) => {
    const state = get();
    const card = state._allCards.find((c) => c.id === cardId);
    const dependency = state._allCards.find((c) => c.id === dependencyId);

    if (!card || !dependency || card.dependsOn.includes(dependencyId)) return;

    const updatedDependsOn = [...card.dependsOn, dependencyId];
    await state.updateCard(cardId, { dependsOn: updatedDependsOn });
  },

  removeDependency: async (cardId, dependencyId) => {
    const state = get();
    const card = state._allCards.find((c) => c.id === cardId);
    const dependency = state._allCards.find((c) => c.id === dependencyId);

    if (!card || !dependency) return;

    const updatedDependsOn = card.dependsOn.filter((id) => id !== dependencyId);
    await state.updateCard(cardId, { dependsOn: updatedDependsOn });
  },

  checkDependencies: (cardId) => {
    const state = get();
    const card = state._allCards.find((c) => c.id === cardId);
    if (!card) return true;

    return card.dependsOn.every((depId) => {
      const depCard = state._allCards.find((c) => c.id === depId);
      return depCard && depCard.status === 'done';
    });
  },

  getBlockingCards: (cardId) => {
    const state = get();
    const card = state._allCards.find((c) => c.id === cardId);
    if (!card) return [];

    return card.dependsOn
      .map((depId) => state._allCards.find((c) => c.id === depId))
      .filter((c): c is Card => c !== undefined && c.status !== 'done');
  },

  getUserVote: (cardId, userId) => {
    const card = get()._allCards.find((c) => c.id === cardId);
    if (!card) return null;

    const userVote = card.sentimentVotes.user_votes.find((vote) => vote.user_id === userId);
    return userVote ? userVote.type : null;
  },

  reorderCards: async (columnId, orderedCardIds) => {
    // Optimistic update on _allCards first
    set((state) => {
      const updatedCardsMap = new Map(orderedCardIds.map((id, index) => [id, index]));
      return {
        _allCards: state._allCards
          .map((card) => {
            if (updatedCardsMap.has(card.id)) {
              return { ...card, position: updatedCardsMap.get(card.id) };
            }
            return card;
          })
          .sort((a, b) => {
            if (a.status === columnId && b.status === columnId) {
              return (a.position || 0) - (b.position || 0);
            }
            return 0;
          }),
      };
    });
    
    // Only apply filter if not in drag operation
    if (!get().isDragInProgress) {
      get()._applyFilter();
    }

    try {
      const positionsToUpdate = orderedCardIds.map((id, index) => ({
        id,
        position: index,
      }));

      await updateCardPositions(positionsToUpdate);

      console.log('Card positions updated successfully in DB.');
    } catch (error: unknown) {
      console.error('Error during reorderCards DB operation:', error);
      set({ error: `Failed to reorder cards: ${errorMessage(error)}` });
      get().initializeCards();
    }
  },

  updateCardPosition: async (cardId, direction) => {
    const { _allCards, reorderCards } = get();
    const cardToMove = _allCards.find((c) => c.id === cardId);

    if (!cardToMove) return;

    const columnCards = _allCards
      .filter((c) => c.status === cardToMove.status)
      .sort((a, b) => (a.position || 0) - (b.position || 0));

    const oldIndex = columnCards.findIndex((c) => c.id === cardId);
    if (oldIndex === -1) return;

    let newIndex = oldIndex;
    if (direction === 'up') {
      newIndex = Math.max(0, oldIndex - 1);
    } else if (direction === 'down') {
      newIndex = Math.min(columnCards.length - 1, oldIndex + 1);
    }

    if (newIndex === oldIndex) return;

    const newOrder = [...columnCards];
    const [removed] = newOrder.splice(oldIndex, 1);
    newOrder.splice(newIndex, 0, removed);

    const orderedCardIds = newOrder.map((c) => c.id);

    await reorderCards(cardToMove.status, orderedCardIds);
  },

  linkGitHubIssue: async (cardId, issueData) => {
    console.log('[cardStore] Attempting to link issue. Card ID:', cardId);
    try {
      const dbUpdates = mapCardUpdatesToDB({ githubIssue: issueData });
      const updatedCard = await updateCardById(cardId, dbUpdates);

      set((state) => ({
        _allCards: state._allCards.map((card) => (card.id === cardId ? updatedCard : card)),
      }));
      get()._applyFilter();

      const currentUser = useUserStore.getState().currentUser;
      const card = get()._allCards.find((c) => c.id === cardId);
      if (currentUser && card) {
        useFeedStore.getState().addEvent({
          type: 'github_connected',
          title: 'GitHub Issue Linked',
          description: `${currentUser.name} linked GitHub issue #${issueData.number} from ${issueData.repo} to "${card.title}"`,
          userId: currentUser.id,
          initiatorId: currentUser.id,
          cardId: card.id,
          sprintId: card.sprintId,
          roadmapId: card.roadmapId,
          githubData: { issueId: issueData.id },
        });
      }
    } catch (error: unknown) {
      console.error('Error linking GitHub issue:', error);
      set({ error: `Failed to link GitHub issue: ${errorMessage(error)}` });
    }
  },

  fetchCardById: async (cardId: string) => {
    return fetchCardById(cardId);
  },

  refreshCardsFromDB: async () => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser?.companyId) {
        console.warn('refreshCardsFromDB: No current user companyId found. Skipping card refresh.');
        set({ isLoading: false });
        return;
      }

      const allCardsFromDb = await fetchCards(currentUser.companyId, null);
      
      // CRITICAL: Deep compare to prevent unnecessary re-renders
      // Only update _allCards if the data actually changed
      const currentCards = get()._allCards;
      
      // Quick length check first
      if (currentCards.length !== allCardsFromDb.length) {
        console.log('📦 [cardStore] refreshCardsFromDB - Card count changed, updating:', {
          old: currentCards.length,
          new: allCardsFromDb.length
        });
        set({ _allCards: allCardsFromDb, isLoading: false, lastFetchedAt: Date.now() });
        get()._applyFilter();
        return;
      }
      
      // Deep compare: check if any card data actually changed
      const hasChanges = allCardsFromDb.some((newCard) => {
        const oldCard = currentCards.find(c => c.id === newCard.id);
        if (!oldCard) return true; // New card
        
        // Compare key fields that would affect display
        return (
          oldCard.title !== newCard.title ||
          oldCard.description !== newCard.description ||
          oldCard.status !== newCard.status ||
          oldCard.assigneeId !== newCard.assigneeId ||
          oldCard.blocked !== newCard.blocked ||
          oldCard.position !== newCard.position ||
          oldCard.startedAt !== newCard.startedAt ||
          oldCard.pausedAt !== newCard.pausedAt ||
          oldCard.totalWorkSeconds !== newCard.totalWorkSeconds ||
          oldCard.is_drift !== newCard.is_drift
        );
      });
      
      if (hasChanges) {
        console.log('📦 [cardStore] refreshCardsFromDB - Data changed, updating');
        set({ _allCards: allCardsFromDb, isLoading: false, lastFetchedAt: Date.now() });
        get()._applyFilter();
      } else {
        console.log('📦 [cardStore] refreshCardsFromDB - No changes detected, keeping existing array reference');
        // Just update the timestamp, don't touch _allCards
        set({ isLoading: false, lastFetchedAt: Date.now() });
      }
      
      console.log('refreshCardsFromDB: Cards refreshed successfully from DB.');
    } catch (error: unknown) {
      console.error('Error refreshing cards from DB:', error);
      set({ error: `Failed to refresh cards: ${errorMessage(error)}`, isLoading: false });
    }
  },

  refreshCardsIfStale: async () => {
    const { lastFetchedAt, isLoading, _allCards } = get();
    const now = Date.now();
    const STALE_THRESHOLD = 30000; // 30 seconds

    // If no cards loaded yet, initialize
    if (_allCards.length === 0) {
      await get().initializeCards();
      return;
    }

    // Skip if already loading or data is fresh
    if (isLoading || (lastFetchedAt && now - lastFetchedAt < STALE_THRESHOLD)) {
      console.log('refreshCardsIfStale: Data is fresh, skipping refresh');
      return;
    }

    console.log('refreshCardsIfStale: Data is stale, refreshing...');
    await get().refreshCardsFromDB();
  },

  /**
   * Setup realtime subscription for cards
   */
  setupRealtimeSubscription: async () => {
    // Clean up existing subscription
    get().cleanupRealtimeSubscription();

    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser?.companyId) {
      console.warn('setupRealtimeSubscription: No current user companyId found. Skipping realtime subscription setup.');
      return;
    }

    // Ensure cards are loaded first
    const { _allCards, isLoading } = get();
    if (_allCards.length === 0 && !isLoading) {
      await get().initializeCards();
    }

    const channel = supabase
      .channel('cards_channel')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'cards',
          filter: `company_id=eq.${currentUser.companyId}`,
        },
        (payload) => {
          // payload.new/old are untyped row bags; map via CardRow
          const newRec = payload.new as Record<string, unknown> | null;
          const oldRec = payload.old as Record<string, unknown> | null;
          console.log('🔔 Real-time card payload received:', {
            eventType: payload.eventType,
            cardId: newRec?.id || oldRec?.id,
            cardTitle: newRec?.title || oldRec?.title,
            timestamp: new Date().toISOString()
          });
          const { eventType, new: newRow, old: oldRow } = payload;

          switch (eventType) {
            case 'INSERT': {
              const newCard = mapCardFromDB(newRow as CardRow);
              set((state) => {
                // addCard already inserted this row. A late insert event must
                // not prepend a second copy (empty title, still a draft).
                if (state._allCards.some((c) => c.id === newCard.id)) return state;
                const updatedAllCards = [newCard, ...state._allCards];
                setTimeout(() => get()._applyFilter(), 0);
                return { _allCards: updatedAllCards };
              });
              break;
            }

            case 'UPDATE': {
              const updatedCard = mapCardFromDB(newRow as CardRow);
              const currentUser = useUserStore.getState().currentUser;
              const existingCard = get()._allCards.find(c => c.id === updatedCard.id);

              // A late echo of the draft save must not put a finalized card
              // back in Draft. That was the production-only Assign bug.
              if (existingCard && existingCard.is_draft === false && updatedCard.is_draft) {
                break;
              }
              
              // December 9, 2025: Handle draft finalization - card might not exist in store yet
              // If card doesn't exist but is now visible to current user, treat as INSERT
              if (!existingCard) {
                const oldCard = oldRow ? mapCardFromDB(oldRow as CardRow) : null;
                const wasDraft = oldCard?.is_draft === true;
                const isNowVisible = !updatedCard.is_draft;
                
                if (wasDraft && isNowVisible) {
                  console.log('🔔 [cardStore] Realtime UPDATE - Draft finalized, adding to store:', {
                    cardId: updatedCard.id,
                    title: updatedCard.title,
                    assigneeId: updatedCard.assigneeId,
                  });
                  // Treat as INSERT - card is now visible to this user
                  set((state) => {
                    const updatedAllCards = [updatedCard, ...state._allCards];
                    setTimeout(() => get()._applyFilter(), 0);
                    return { _allCards: updatedAllCards };
                  });
                  break;
                }
                
                // Otherwise, card doesn't exist in store and shouldn't be added
                console.log('🔔 [cardStore] Realtime UPDATE - Card not in store, skipping:', updatedCard.id);
                break;
              }
              
              // Check if this update is from our own action (status/position change from moveCardOptimistic)
              // If status and position match what we already have, and we're the assignee, skip to prevent reload
              const isOwnStatusUpdate = 
                currentUser &&
                updatedCard.assigneeId === currentUser.id &&
                existingCard.status === updatedCard.status &&
                existingCard.position === updatedCard.position &&
                existingCard.title === updatedCard.title &&
                existingCard.description === updatedCard.description;
              
              // Check if this is a time tracking only update
              // Compare using != null to handle both null and undefined
              const timeTrackingChanged = 
                (existingCard.startedAt != null) !== (updatedCard.startedAt != null) ||
                (existingCard.pausedAt != null) !== (updatedCard.pausedAt != null) ||
                existingCard.lastWorkSessionStart !== updatedCard.lastWorkSessionStart ||
                existingCard.totalWorkSeconds !== updatedCard.totalWorkSeconds ||
                (existingCard.endedAt != null) !== (updatedCard.endedAt != null);
              
              const isTimeTrackingUpdate = 
                existingCard.status === updatedCard.status &&
                existingCard.position === updatedCard.position &&
                existingCard.assigneeId === updatedCard.assigneeId &&
                existingCard.blocked === updatedCard.blocked &&
                existingCard.title === updatedCard.title &&
                existingCard.description === updatedCard.description &&
                timeTrackingChanged;
              
              // Skip if it's our own status update or time tracking update (already handled optimistically)
              if (currentUser && updatedCard.assigneeId === currentUser.id && (isOwnStatusUpdate || isTimeTrackingUpdate)) {
                console.log('📡 [cardStore] Realtime UPDATE - Skipping own update (already handled optimistically)', {
                  isOwnStatusUpdate,
                  isTimeTrackingUpdate,
                  timeTrackingChanged
                });
                break;
              }
              
              set((state) => {
                const updatedAllCards = state._allCards.map((c) =>
                  c.id === updatedCard.id ? updatedCard : c
                );
                
                // Update cards array directly to avoid full filter (prevents reloads)
                const updatedCards = updatedAllCards;
                
                return { _allCards: updatedAllCards, cards: updatedCards };
              });
              break;
            }

            case 'DELETE': {
              set((state) => {
                const updatedAllCards = state._allCards.filter((c) => c.id !== oldRow.id);
                // Apply filter after state update
                setTimeout(() => get()._applyFilter(), 0);
                return { _allCards: updatedAllCards };
              });
              break;
            }
          }
        }
      )
      .subscribe();

    set({ realtimeChannel: channel });
    console.log('Cards realtime subscription established');
  },

  /**
   * Cleanup realtime subscription
   */
  cleanupRealtimeSubscription: () => {
    const { realtimeChannel } = get();
    if (realtimeChannel) {
      supabase.removeChannel(realtimeChannel);
      set({ realtimeChannel: null });
      console.log('Cards realtime subscription cleaned up');
    }
  },

  setDragInProgress: (inProgress: boolean) => {
    set({ isDragInProgress: inProgress });
    // Apply filter when drag operation ends
    if (!inProgress) {
      get()._applyFilter();
    }
  },

  // December 4, 2025: Archive support
  archiveCard: async (cardId: string) => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      const card = get()._allCards.find((c) => c.id === cardId);
      if (!card) throw new Error('Card not found');

      const now = new Date().toISOString();

      // Update in database
      const { error } = await supabase
        .from('cards')
        .update({
          archived_at: now,
          archived_by: currentUser.id,
        })
        .eq('id', cardId);

      if (error) throw error;

      // Update local state
      set((state) => ({
        _allCards: state._allCards.map((c) =>
          c.id === cardId
            ? { ...c, archivedAt: now, archivedBy: currentUser.id }
            : c
        ),
        isLoading: false,
      }));
      get()._applyFilter();

      // Add feed event
      useFeedStore.getState().addEvent({
        type: feedEventType(feedKindForCardType(card.type) === 'task' ? 'task_updated' : 'idea_updated'),
        title: 'Card Archived',
        description: `${currentUser.name} archived "${card.title}"`,
        userId: currentUser.id,
        initiatorId: currentUser.id,
        cardId: card.id,
        sprintId: card.sprintId,
        roadmapId: card.roadmapId,
      });

      console.log('📦 Card archived:', cardId);
    } catch (error: unknown) {
      console.error('Error archiving card:', error);
      set({ error: `Failed to archive card: ${errorMessage(error)}`, isLoading: false });
    }
  },

  unarchiveCard: async (cardId: string) => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      const card = get()._allCards.find((c) => c.id === cardId);
      if (!card) throw new Error('Card not found');

      // Update in database
      const { error } = await supabase
        .from('cards')
        .update({
          archived_at: null,
          archived_by: null,
        })
        .eq('id', cardId);

      if (error) throw error;

      // Update local state
      set((state) => ({
        _allCards: state._allCards.map((c) =>
          c.id === cardId
            ? { ...c, archivedAt: undefined, archivedBy: undefined }
            : c
        ),
        isLoading: false,
      }));
      get()._applyFilter();

      // Add feed event
      useFeedStore.getState().addEvent({
        type: feedEventType(feedKindForCardType(card.type) === 'task' ? 'task_updated' : 'idea_updated'),
        title: 'Card Restored',
        description: `${currentUser.name} restored "${card.title}" from archive`,
        userId: currentUser.id,
        initiatorId: currentUser.id,
        cardId: card.id,
        sprintId: card.sprintId,
        roadmapId: card.roadmapId,
      });

      console.log('📤 Card unarchived:', cardId);
    } catch (error: unknown) {
      console.error('Error unarchiving card:', error);
      set({ error: `Failed to unarchive card: ${errorMessage(error)}`, isLoading: false });
    }
  },
}));

// Tu Metrics - December 5, 2025
Object.assign(useCardStore, { tuMetrics: {
  id: 'card-store',
  tu1: 0.68,  // Large store (~2000 lines) but comprehensive
  tu2: 0.75,  // Well-structured with clear action separation
  tu3: 8000,  // Core data layer for all card operations
  breakageResistance: 1.6,  // Many consumers depend on this API
  notes: 'December 2025: Added archiving (archiveCard/unarchiveCard), reminder support, ' +
         'idea assignment notifications (idea_updated event type). ' +
         'Fixed convertCardType to use direct update instead of non-existent RPC. ' +
         'Idea notifications now mirror task notification pattern.',
  lastRefactor: '2025-12-05'
}});

export default useCardStore;
